import {
  addToast,
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextAreaInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { TranslationsKeys } from '../../../../locales/translations';
import { createWarningRecord } from '../../actions';
import { useWarningsSummaryDetailsFormLoaderData } from '../../loaders/StudentWarningsSummaryDetailsFormLoader';
import { useWarningsSummaryDetailsLoader } from '../../loaders/StudentWarningsSummaryDetailsLoader';
import { StudentWarningsGroupType } from '../../types';

export type HealthWarningGroupRecord = {
  id: number;
  type: string;
  subType: string | null;
  state: string;
  date: string;
  notes: string | null;
};

export type HealthWarningGroupModalMethods = {
  open: (record?: HealthWarningGroupRecord) => void;
  close: () => void;
};

type HealthWarningGroupModalProps = {
  onSave?: () => void;
};

type FormFields = {
  type: SelectOptionType | null;
  subType: SelectOptionType | null;
  state: SelectOptionType | null;
  date: string;
  notes?: string | null;
};

const DEFAULT_VALUES: FormFields = {
  type: null,
  subType: null,
  state: null,
  date: '',
  notes: null,
};

const HealthWarningGroupModal = (
  { onSave }: HealthWarningGroupModalProps,
  ref: Ref<HealthWarningGroupModalMethods>,
) => {
  const data = useWarningsSummaryDetailsFormLoaderData();
  const { query } = useWarningsSummaryDetailsLoader();
  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS_DETAILS);
  const [visible, setVisible] = useState(false);
  const [record, setRecord] = useState<HealthWarningGroupRecord>();

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const recordType = watch('type');

  const { action: handleClickSave, loading } = useActionKitImp(
    createWarningRecord,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`group.health.modal.success`),
        });
        !!onSave && onSave();
        setVisible(false);
        setRecord(undefined);
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`group.health.modal.failed`),
          });
        },
      },
    },
  );

  const handleOpen = useCallback(
    (nextRecord?: HealthWarningGroupRecord) => {
      if (nextRecord) {
        reset({
          type:
            data?.healthRecordTypes.find(
              (option) => option.value === nextRecord.type,
            ) || null,
          subType:
            data?.healthRecordSubTypes.find(
              (option) => option.value === nextRecord.subType,
            ) || null,
          state:
            data?.healthRecordStates.find(
              (option) => option.value === nextRecord.state,
            ) || null,
          date: nextRecord.date,
          notes: nextRecord.notes,
        });
      } else {
        reset(DEFAULT_VALUES);
      }
      setVisible(true);
      setRecord(nextRecord);
    },
    [data, reset],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setRecord(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }
      handleClickSave(StudentWarningsGroupType.Health, {
        ...data,
        studyPlanEnrollmentId: query.studyPlanEnrollmentId,
        type: data?.type?.value,
        subType: data?.subType?.value,
        state: data?.state?.value,
      });
    },
    [loading, query, handleClickSave],
  );

  return (
    <Modal isOpen={!!visible} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {record?.id
                ? t(`group.health.modal.titleEdit`)
                : t(`group.health.modal.title`)}
            </h2>
            <p className="fs-16 w-100 text-center">
              {record?.id
                ? t(`group.health.modal.descriptionEdit`)
                : t(`group.health.modal.description`)}
            </p>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="type"
              options={data?.healthRecordTypes || []}
              label={t(`group.health.table.type`)}
              isClearable={false}
              rules={{ required: true }}
              disabled={!!record?.id || loading}
            />
          </Col>
          {recordType?.value === 'other' && (
            <Col xs={12} md={4}>
              <Select
                control={control}
                name="subType"
                options={data?.healthRecordSubTypes || []}
                label={t(`group.health.table.subType`)}
                isClearable={false}
                rules={{ required: true }}
                disabled={!!record?.id || loading}
              />
            </Col>
          )}
          <Col xs={12} md={4}>
            <Select
              control={control}
              name="state"
              options={data?.healthRecordStates || []}
              label={t(`group.health.table.state`)}
              isClearable={false}
              rules={{ required: true }}
              disabled={!!record?.id || loading}
            />
          </Col>
          <Col xs={12} md={4}>
            <DateInput
              control={control}
              name="date"
              label={t(`group.health.table.date`)}
              rules={{ required: true }}
              maxDate={new Date()}
              disabled={!!record?.id || loading}
            />
          </Col>
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`group.health.table.notes`)}
              rules={{ required: false }}
              disabled={!!record?.id || loading}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={record?.id ? 12 : 6}>
            <Button
              text={
                record?.id
                  ? t('common:actions.close')
                  : t('common:actions.cancel')
              }
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          {!record?.id && (
            <Col xs={12} md={6}>
              <Button
                text={t('common:actions.add')}
                type="button"
                fullwidth
                onClick={handleSubmit(handleSave)}
                loading={loading}
              />
            </Col>
          )}
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(HealthWarningGroupModal);
